import { Form, Formik } from 'formik';
import { Category } from '../../../models/Category/Category';
import { useQuery } from '@tanstack/react-query';
import { TreeSelect } from 'antd';
import { InputField } from '../../components/InputField/InputField';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { ErrorMessageLabel } from '../../components/ErrorMessageLabel/ErrorMessageLabel';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useRef, useState } from 'react';
import { FaPlus as PlusIcon, FaTimes as TrashIcon } from 'react-icons/fa';
import { MediaLibrarySelector } from '../../components/MediaLibrarySelector/MediaLibrarySelector';

import style from './create-category.module.scss';

const createCategoryValidationSchema = Yup.object({
  parent_id: Yup.number().nullable(),
  name: Yup.string().required('Kategorija mora imati ime'),
  price: Yup.number().required(),
  penalty_price: Yup.number().required('Unesite kaznenu cenu'),
  cover_image_id: Yup.number().nullable()
});

function CreateCategory() {
  type DataType = {
    parent_id: number | null;
    name: string;
    price: number;
    penalty_price: number;
    cover_image_id: number | null;
  };

  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const { data: categories, refetch } = useQuery(['categories'], () =>
    Category.all()
  );

  const initialValues: DataType = {
    parent_id: null,
    name: '',
    price: 0,
    penalty_price: 0,
    cover_image_id: null
  };

  return (
    <>
      <h1 className="cs-title">Kreiranje kategorije</h1>
      <Row>
        <Col>
          <Formik
            initialValues={initialValues}
            validationSchema={createCategoryValidationSchema}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setSubmitting(true);
              Category.store(values)
                .then(() => {
                  refetch();
                  resetForm();
                  setImagePreview(null);
                  toast.success('Uspešno kreirana kategorija'); // Success toast
                })
                .catch((error) => {
                  toast.error(
                    error?.response?.data?.message ||
                      'Greška pri kreiranju kategorije.' // Error toast
                  );
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
          >
            {({ setFieldValue, isSubmitting, values }) => (
              <Form autoComplete="off" className="cs-form-wrapper">
                <div className={style.form}>
                  <div className={style.formCol}>
                    <FormGroup>
                      <FormLabel>Bazna Kategorija</FormLabel>
                      <TreeSelect
                        style={{ width: '100%' }}
                        showSearch
                        treeDefaultExpandAll
                        placeholder="roditelj kategorija"
                        treeData={categories ?? []}
                        value={values.parent_id}
                        onChange={(value) =>
                          setFieldValue('parent_id', value, true)
                        }
                      />
                      <ErrorMessageLabel name="parent_id" component="div" />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Ime Kategorije</FormLabel>
                      <InputField
                        type="text"
                        name="name"
                        placeholder="Ime kategorije"
                        autoComplete="off"
                      />
                      <ErrorMessageLabel name="name" component="div" />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>
                        Cena proizvoda iz kategorije (
                        {process.env.REACT_APP_DEFAULT_CURRENCY})
                      </FormLabel>
                      <InputField
                        type="number"
                        step={1}
                        min={0}
                        name="price"
                        placeholder="Cena"
                        autoComplete="off"
                      />
                      <ErrorMessageLabel name="price" component="div" />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>
                        Kaznena cena za oštećenje (
                        {process.env.REACT_APP_DEFAULT_CURRENCY})
                      </FormLabel>
                      <InputField
                        type="number"
                        step={1}
                        min={0}
                        name="penalty_price"
                        placeholder="Kaznena cena"
                        autoComplete="off"
                      />
                      <ErrorMessageLabel name="penalty_price" component="div" />
                    </FormGroup>
                  </div>

                  <div className={style.formCol}>
                    <FormGroup>
                      <FormLabel>Dodaj sliku</FormLabel>
                      {imagePreview ? (
                        <FormGroup className={style.imagePreview}>
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className={style.image}
                          />
                          <button
                            type="button"
                            className={style.removeImage}
                            onClick={() => {
                              setImagePreview(null);
                              setFieldValue('image', null);
                            }}
                          >
                            <TrashIcon />
                          </button>
                        </FormGroup>
                      ) : (
                        <div className={style.imagePreviewEmpty}>
                          <p>Niste izabrali sliku</p>
                          <button
                            type="button"
                            className={style.addImageButton}
                            onClick={() => setIsMediaLibraryOpen(true)}
                          >
                            <PlusIcon />
                          </button>
                        </div>
                      )}
                      {isMediaLibraryOpen && (
                        <MediaLibrarySelector
                          isOpen={isMediaLibraryOpen}
                          onClose={() => setIsMediaLibraryOpen(false)}
                          onSelect={(media) => {
                            if (media && 'id' in media) {
                              setFieldValue('cover_image_id', media.id);

                              // If you want to show preview:
                              setImagePreview(media.fullUrl ?? media.url);

                              setIsMediaLibraryOpen(false);
                            } else {
                              console.error(
                                'Selected image is not valid',
                                media
                              );
                            }
                          }}
                        />
                      )}
                      <ErrorMessageLabel name="image" />
                    </FormGroup>
                  </div>
                </div>

                <div className={style.footer}>
                  <ButtonPrimary type="submit" disabled={isSubmitting}>
                    Sačuvaj
                  </ButtonPrimary>
                </div>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </>
  );
}

export default CreateCategory;
